import { useEffect, useRef, useState } from 'react';
import {
    AreaSeries,
    BaselineSeries,
    ColorType,
    CrosshairMode,
    createChart,
    createSeriesMarkers,
    type IChartApi,
    type MouseEventParams,
    type SeriesMarker,
    type Time
} from 'lightweight-charts';
import { formatSignedCurrency, formatSignedPercent, formatUnsignedCurrency, signedToneClass } from '../performanceFormat';
import { formatWeekdayDate, type EquityCurvePoint } from '../performanceUtils';

interface EquityDrawdownChartProps {
    points: EquityCurvePoint[];
    /** `equity` plots portfolio equity with a drawdown pane; `strategy` plots cumulative strategy P&L. */
    mode: 'equity' | 'strategy';
    lineColor: string;
    height?: number;
    onSelectDay?: (date: string) => void;
}

interface HoverState {
    point: EquityCurvePoint;
    left: number;
    top: number;
}

const DRAWDOWN_PANE_HEIGHT = 84;

const withAlpha = (hex: string, alpha: number) => {
    const value = hex.replace('#', '');
    const r = Number.parseInt(value.slice(0, 2), 16);
    const g = Number.parseInt(value.slice(2, 4), 16);
    const b = Number.parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Equity curve with trade-close markers and, for the whole portfolio, a drawdown pane beneath. Hovering shows the
 * day's figures; clicking a day hands it to the caller (the calendar's day drawer).
 */
const EquityDrawdownChart = ({ points, mode, lineColor, height = 300, onSelectDay }: EquityDrawdownChartProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onSelectDayRef = useRef(onSelectDay);
    const [hover, setHover] = useState<HoverState | null>(null);

    useEffect(() => {
        onSelectDayRef.current = onSelectDay;
    }, [onSelectDay]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || points.length === 0) return;

        const pointByDate = new Map(points.map((point) => [point.date, point]));
        const totalHeight = mode === 'equity' ? height + DRAWDOWN_PANE_HEIGHT : height;

        const chart: IChartApi = createChart(container, {
            autoSize: true,
            height: totalHeight,
            layout: {
                background: { type: ColorType.Solid, color: '#121212' },
                textColor: '#a1a1aa',
                attributionLogo: false,
                panes: { separatorColor: '#27272a', separatorHoverColor: '#3f3f46', enableResize: false }
            },
            grid: { vertLines: { color: '#1f1f23' }, horzLines: { color: '#1f1f23' } },
            rightPriceScale: { borderColor: '#27272a' },
            timeScale: { borderColor: '#27272a', timeVisible: false, secondsVisible: false },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: { color: '#52525b', labelBackgroundColor: '#27272a' },
                horzLine: { visible: false }
            },
            handleScale: { mouseWheel: false, pinch: false },
            handleScroll: { mouseWheel: false }
        });

        const series = chart.addSeries(AreaSeries, {
            lineColor,
            lineWidth: 2,
            topColor: withAlpha(lineColor, 0.28),
            bottomColor: withAlpha(lineColor, 0),
            priceLineVisible: false,
            lastValueVisible: true,
            crosshairMarkerRadius: 4,
            priceFormat: { type: 'price', precision: 0, minMove: 1 }
        });

        series.setData(points.map((point) => ({ time: point.date as Time, value: point.value })));

        const markers: SeriesMarker<Time>[] = [];

        for (const point of points) {
            if (point.closedCount > 0) {
                markers.push({
                    time: point.date as Time,
                    position: 'inBar',
                    shape: 'circle',
                    color: point.realizedPnl >= 0 ? '#29a740' : '#ef4444',
                    size: 1
                });
            }

            if (mode === 'equity' && point.fundingFlow !== 0) {
                markers.push({
                    time: point.date as Time,
                    position: 'aboveBar',
                    shape: 'square',
                    color: '#60a5fa',
                    size: 1,
                    text: 'Funding'
                });
            }
        }

        if (markers.length) createSeriesMarkers(series, markers);

        if (mode === 'equity') {
            const drawdownSeries = chart.addSeries(
                BaselineSeries,
                {
                    baseValue: { type: 'price', price: 0 },
                    topLineColor: 'rgba(0, 0, 0, 0)',
                    topFillColor1: 'rgba(0, 0, 0, 0)',
                    topFillColor2: 'rgba(0, 0, 0, 0)',
                    bottomLineColor: '#ef4444',
                    bottomFillColor1: 'rgba(239, 68, 68, 0.05)',
                    bottomFillColor2: 'rgba(239, 68, 68, 0.35)',
                    lineWidth: 1,
                    priceLineVisible: false,
                    lastValueVisible: false,
                    crosshairMarkerVisible: false,
                    priceFormat: { type: 'custom', formatter: (value: number) => `${value.toFixed(1)}%`, minMove: 0.01 }
                },
                1
            );

            drawdownSeries.setData(points.map((point) => ({ time: point.date as Time, value: point.drawdownPercent ?? 0 })));
            chart.panes()[1]?.setHeight(DRAWDOWN_PANE_HEIGHT);
        }

        chart.timeScale().fitContent();

        const handleCrosshairMove = (param: MouseEventParams<Time>) => {
            const date = typeof param.time === 'string' ? param.time : null;
            const point = date ? pointByDate.get(date) : undefined;

            if (!point || !param.point) {
                setHover(null);
                return;
            }

            setHover({ point, left: param.point.x, top: param.point.y });
        };

        const handleClick = (param: MouseEventParams<Time>) => {
            if (typeof param.time === 'string' && pointByDate.has(param.time)) onSelectDayRef.current?.(param.time);
        };

        chart.subscribeCrosshairMove(handleCrosshairMove);
        chart.subscribeClick(handleClick);

        return () => {
            chart.unsubscribeCrosshairMove(handleCrosshairMove);
            chart.unsubscribeClick(handleClick);
            chart.remove();
            setHover(null);
        };
    }, [points, mode, lineColor, height]);

    const totalHeight = mode === 'equity' ? height + DRAWDOWN_PANE_HEIGHT : height;

    return (
        <div className="relative">
            <div className="w-full cursor-pointer" ref={containerRef} style={{ height: `${totalHeight}px` }} />
            {hover && (
                <div
                    className="pointer-events-none absolute z-10 rounded-md border border-zinc-700 bg-[#09090b] px-3 py-2 text-xs text-zinc-200 shadow-lg"
                    style={{
                        left: Math.min(hover.left + 14, Math.max(0, (containerRef.current?.clientWidth ?? 0) - 230)),
                        top: Math.max(0, hover.top - 12)
                    }}
                >
                    <div className="font-semibold text-white">{formatWeekdayDate(hover.point.date)}</div>
                    <div>
                        <span className="text-zinc-400">{mode === 'equity' ? 'Equity' : 'Cumulative'}</span>{' '}
                        {mode === 'equity' ? formatUnsignedCurrency(hover.point.value) : formatSignedCurrency(hover.point.value)}
                    </div>
                    <div>
                        <span className="text-zinc-400">Day</span>{' '}
                        <span className={signedToneClass(hover.point.dayPnl)}>{formatSignedCurrency(hover.point.dayPnl)}</span>
                    </div>
                    {hover.point.closedCount > 0 && (
                        <div>
                            <span className="text-zinc-400">Closed</span> {hover.point.closedCount} ·{' '}
                            <span className={signedToneClass(hover.point.realizedPnl)}>
                                {formatSignedCurrency(hover.point.realizedPnl)}
                            </span>
                        </div>
                    )}
                    {mode === 'equity' && hover.point.drawdownPercent !== null && hover.point.drawdownPercent < 0 && (
                        <div>
                            <span className="text-zinc-400">Drawdown</span>{' '}
                            <span className="text-red-400">{formatSignedPercent(hover.point.drawdownPercent)}</span>
                        </div>
                    )}
                    {hover.point.fundingFlow !== 0 && (
                        <div>
                            <span className="text-zinc-400">Funding</span>{' '}
                            <span className="text-blue-400">{formatSignedCurrency(hover.point.fundingFlow, 0)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EquityDrawdownChart;
